import { cn } from "@faire/react/tools/cn";
import { componentCls } from "@faire/slate/lib/tracking";
import { tsv } from "@faire/slate/lib/tsv";
import { IDividerWebProps } from "@faire/slate/private/divider/types";
import {
  TwResponsiveValues,
  TwScaleSuffix,
  TwSizeSuffix,
} from "@faire/slate/private/shared/tw-types";
import { toTwClass } from "@faire/slate/private/shared/tw-utils";
import React from "react";

type IProps = {
  /**
   * **web**
   * Margin around the divider. Pass an array to combine multiple responsive
   * values (e.g. `["my-4", "md:my-8"]`).
   *
   * @default "m-0"
   */
  margin?: TwResponsiveValues<
    "m" | "mt" | "mb" | "ml" | "mr" | "mx" | "my",
    TwScaleSuffix
  >;
  /**
   * **web**
   * Width of the divider. Pass an array to combine multiple responsive
   * values (e.g. `["w-full", "md:w-3/4"]`).
   *
   * @default "w-full"
   */
  width?: TwResponsiveValues<"w", TwSizeSuffix>;
  /**
   * **web**
   * Display utility for the divider. Use to hide/show at breakpoints. Pass an
   * array to combine multiple responsive values (e.g. `["hidden", "md:block"]`).
   *
   * @default "block"
   */
  display?: TwResponsiveValues<"block" | "hidden", "">;
  // DO NOT ADD any new prop without aligning with Slate team first
} & IDividerWebProps;

/**
 * A horizontal divider line used to separate content.
 *
 * See [migration guide](migration-guide.mdx) for more information.
 *
 * {@link https://www.notion.so/faire/Divider-2242efb5c25a809ba346c5304651c53c | Slate/Divider}
 */
export const Divider: React.FC<IProps> = ({
  margin = "m-0",
  width = "w-full",
  display = "block",
  ...props
}) => (
  <hr
    aria-hidden
    {...props}
    className={dividerStyle.cls({
      className: cn(
        componentCls(),
        toTwClass(margin),
        toTwClass(width),
        toTwClass(display)
      ),
    })}
    data-component-name="Slate/Divider"
  />
);

const dividerStyle = tsv({
  base: "border-fs-border-muted border-0 border-t border-solid",
});
